import type { DiningWidgetInfoResponse } from '@sevenrooms/api'
import type { PromoCode, BillingAddress, RedemptionData, ReservationWidget, MessageChannelOptionsEnum } from '@sevenrooms/domain'
import { ReservationWidget as ReservationWidgetConst } from '@sevenrooms/domain/constants'
import { allCountries, iso2Lookup, type LanguageCode, type CountryCode, isCountryCode } from '@sevenrooms/locales'
import { DateOnly, Formats, legacyParseAdapter, TimeOnly, Timezone, isValid } from '@sevenrooms/timepiece'
import type { FormStateActual } from './reservationFormSlice'
import type { AvailabilityTimeWithUpSellCost, ExperienceWithAvailability } from '../../hooks/useAvailability'
import type { SchemaCategories } from '../../utils/upgradesUtils'
import type { OtherVenueInfo } from '@/domain'

export type VenueInfo = DiningWidgetInfoResponse['venueInfo']
export type WidgetSettings = DiningWidgetInfoResponse['widgetSettings']
export type ClientInfo = DiningWidgetInfoResponse['clientInfo']

const searchParams = new URLSearchParams(document.location.search)

export interface LockedFields {
  firstName: boolean
  lastName: boolean
  email: boolean
  phoneNumber: boolean
  postalCode: boolean
}

export const getInitialReservationFormStateState = (
  venueInfo?: VenueInfo,
  widgetSettings?: WidgetSettings,
  clientInfo?: ClientInfo
): {
  partySize: number
  startDate: string
  duration?: number
  privateEventsPartySize: number | null
  privateEventsStartDate: string | undefined
  privateEventsStartTime: string
  privateEventsHaloTimeIntervalMinutes: number
  startTime: string
  haloTimeIntervalMinutes: number
  venues: string | null
  searchTab: ReservationWidget.SearchTab
  selectedTimeSlot: AvailabilityTimeWithUpSellCost | undefined
  selectedExperience: ExperienceWithAvailability | undefined
  selectedOtherVenue: OtherVenueInfo | undefined
  actual: FormStateActual | undefined
  categories: SchemaCategories
  birthdayDay: string | null
  birthdayMonth: string | null
  emailAddress: string
  firstName: string
  gratuityPercentage: number | null
  lastName: string
  phoneNumber?: string
  postalCode: string
  preferredLanguage: LanguageCode
  preferredMessagingChannel?: MessageChannelOptionsEnum
  agreedToBookingPolicy: boolean
  agreedCustomCheckoutPolicy: boolean
  agreedToVenueGroupMarketingOptIn: boolean
  agreedToTailoredCommunicationOptIn: boolean
  agreedToVenueSpecificMarketingOptIn: boolean
  agreedToVenueSmsMarketingOptIn: boolean
  agreedToGroupBookingsPolicy: boolean
  agreedToAboveAgeConsentOn: boolean
  agreedToReservationSmsOptIn: boolean
  agreedToGdprDietaryOptIn: boolean
  phoneCountryCode: CountryCode
  phoneDialCode: string
  notes: string
  timeRange: {
    startTime: string
    endTime: string
  } | null
  startAndEndTime: string | null
  trackingSlug?: string
  clientId?: string
  promoCode?: PromoCode
  billingAddress?: BillingAddress
  salutation: string | null
  redemptionData?: RedemptionData
  champagnePreferenceTags: string[]
  clientDietaryPreferenceTags: string[]
  liquorPreferenceTags: string[]
  partyDietaryPreferenceTags: string[]
  specialOccasionTags: string[]
  customQuestionAnswer: string
  lockedFields: LockedFields
  durationPickerEnabled: boolean
  privateEventName: string | null
} => {
  const {
    minGuests,
    maxGuests,
    maxDaysOut,
    isDefaultCustomCheckoutPolicyOn,
    isVenueGroupMarketingPolicyDefaultOn,
    tailoredCommunicationOn,
    isTailoredCommunicationPolicyDefaultOn,
    isVenueSpecificMarketingPolicyDefaultOn,
    isVenueSmsMarketingPolicyDefaultOn,
    reservationSmsOptInOn,
    displayReservationSmsOptIn,
    isGroupBookingsPolicyDefaultOn,
    minimumSearchDuration,
    maximumSearchDuration,
    defaultSearchDuration,
    durationPickerDefaultEnabled,
  } = widgetSettings ?? {
    durationPickerDefaultEnabled: false,
    minimumSearchDuration: 30,
    maximumSearchDuration: 120,
    minGuests: 1,
    maxGuests: 20,
    maxDaysOut: 225,
  }

  const {
    venueToday,
    venueLanguages,
    countryCode = 'us',
    reservationSmsEnabled,
    newResWidgetPrivateEventsEnabled,
    countOfPrivateEvents,
    isWhatsappEnabled,
    defaultMessageChannel,
    newResWidgetExperiencesEnabled,
    countOfExperiences,
  } = venueInfo ?? {}
  const currentDateAtVenue = DateOnly.fromSafe(venueToday)?.toJsDate() || new Date()
  let preferredLanguageLocalStorage = window.localStorage.getItem(ReservationWidgetConst.PreferredLanguageCookie)
  if (preferredLanguageLocalStorage) {
    preferredLanguageLocalStorage = JSON.parse(preferredLanguageLocalStorage)
  }
  const preferredLanguage = (preferredLanguageLocalStorage as LanguageCode) || venueLanguages?.find(lang => lang.isDefault)?.value || 'en'
  const firstName = clientInfo?.firstName ?? ''
  const lastName = clientInfo?.lastName ?? ''
  const phoneCountryCode = isCountryCode(clientInfo?.phoneNumberLocale) ? clientInfo.phoneNumberLocale : countryCode
  const phoneDialCode = allCountries[iso2Lookup[phoneCountryCode]]?.dialCode ?? ''
  let phoneNumber = ''
  if (clientInfo?.phoneNumberLocalized) {
    phoneNumber = `+${phoneDialCode}${clientInfo?.phoneNumberLocalized}`
  }
  const emailAddress = clientInfo?.email ?? ''
  const postalCode = clientInfo?.postalCode ?? ''
  const birthdayMonth = adjustBirthdayString(clientInfo?.birthdayMonth)
  const birthdayDay = adjustBirthdayString(clientInfo?.birthdayDay)
  const lockedFields = clientInfo?.lockedFields
  const durationPickerEnabled =
    !(searchParams.get('duration_picker') === 'false') && (searchParams.get('duration_picker') === 'true' || !!durationPickerDefaultEnabled)
  const devMode = searchParams.get('devMode') === 'true'

  return {
    actual: undefined,
    partySize: getInitialPartySize({ minGuests, maxGuests }),
    startDate: getStartDate({ currentDateAtVenue, maxDaysOut }),
    startTime: getInitialTime(),
    durationPickerEnabled,
    duration: durationPickerEnabled
      ? getInitialDuration({ minimumSearchDuration, maximumSearchDuration, defaultSearchDuration })
      : undefined,
    searchTab: getInitialSearchTab(
      !!newResWidgetPrivateEventsEnabled && !!countOfPrivateEvents,
      (!!newResWidgetExperiencesEnabled && !!countOfExperiences) || devMode
    ),
    selectedTimeSlot: undefined,
    selectedExperience: undefined,
    selectedOtherVenue: undefined,
    haloTimeIntervalMinutes: getInitialHalo(),
    venues: searchParams.get('venues'),
    categories: {},
    birthdayDay,
    birthdayMonth,
    emailAddress,
    firstName,
    gratuityPercentage: null,
    lastName,
    phoneNumber,
    postalCode,
    preferredLanguage,
    agreedToBookingPolicy: false,
    agreedCustomCheckoutPolicy: isDefaultCustomCheckoutPolicyOn || false,
    agreedToGroupBookingsPolicy: isGroupBookingsPolicyDefaultOn || false,
    agreedToVenueGroupMarketingOptIn: isVenueGroupMarketingPolicyDefaultOn || false,
    agreedToTailoredCommunicationOptIn: (tailoredCommunicationOn && isTailoredCommunicationPolicyDefaultOn) || false,
    agreedToVenueSpecificMarketingOptIn: isVenueSpecificMarketingPolicyDefaultOn || false,
    agreedToVenueSmsMarketingOptIn: isVenueSmsMarketingPolicyDefaultOn || false,
    agreedToAboveAgeConsentOn: false,
    agreedToReservationSmsOptIn: (reservationSmsEnabled && displayReservationSmsOptIn && reservationSmsOptInOn) || false,
    agreedToGdprDietaryOptIn: false,
    phoneCountryCode,
    phoneDialCode,
    notes: '',
    timeRange: null,
    startAndEndTime: null,
    trackingSlug: searchParams.get('tracking') || undefined,
    clientId: searchParams.get('client_id') || undefined,
    billingAddress: undefined,
    salutation: null,
    redemptionData: undefined,
    privateEventsPartySize: null,
    privateEventsStartDate: getStartDate({ currentDateAtVenue, maxDaysOut, ignoreSearchParams: true }),
    privateEventsStartTime: ReservationWidgetConst.AllTimesOption,
    privateEventsHaloTimeIntervalMinutes: ReservationWidgetConst.HaloMinutesOptions[
      ReservationWidgetConst.HaloMinutesOptions.length - 1
    ] as number,
    specialOccasionTags: [],
    clientDietaryPreferenceTags: [],
    partyDietaryPreferenceTags: [],
    champagnePreferenceTags: [],
    liquorPreferenceTags: [],
    customQuestionAnswer: '',
    lockedFields: {
      firstName: lockedFields?.firstName || false,
      lastName: lockedFields?.lastName || false,
      email: lockedFields?.email || false,
      phoneNumber: lockedFields?.phoneNumber || false,
      postalCode: lockedFields?.postalCode || false,
    },
    preferredMessagingChannel: isWhatsappEnabled ? defaultMessageChannel : undefined,
    privateEventName: '',
  }
}

export const getStartDate = ({
  currentDateAtVenue,
  maxDaysOut,
  ignoreSearchParams,
}: {
  currentDateAtVenue: Date
  maxDaysOut: number
  ignoreSearchParams?: boolean
}): string => {
  const startDateParam = ignoreSearchParams ? undefined : searchParams.get('date')

  if (!startDateParam) {
    return currentDateAtVenue.toISOString()
  }

  const dateValue = DateOnly.fromSafe(startDateParam)?.toJsDate()

  if (!dateValue) {
    return currentDateAtVenue.toISOString()
  }
  const maxDateValue = DateOnly.fromDate(currentDateAtVenue).toJsDate()
  maxDateValue.setDate(currentDateAtVenue.getDate() + maxDaysOut - 1)

  if (dateValue < currentDateAtVenue) {
    return currentDateAtVenue.toISOString()
  } else if (dateValue > maxDateValue) {
    return maxDateValue.toISOString()
  }

  return dateValue.toISOString()
}

export const DEFAULT_PARTY_SIZE = 2

export function getInitialPartySize({ minGuests, maxGuests }: { minGuests: number; maxGuests: number }) {
  let partySize = DEFAULT_PARTY_SIZE

  const partySizeParam = searchParams.get('party_size')
  if (partySizeParam) {
    const partySizeValue = Number.parseInt(partySizeParam)
    if (!Number.isNaN(partySizeValue)) {
      partySize = Math.min(partySizeValue, maxGuests)
    }
  }

  return Math.max(partySize, minGuests)
}

function getInitialTime() {
  const timeParam = searchParams.get('start_time')
  if (timeParam) {
    const utcTime = TimeOnly.fromSafe(timeParam)
    if (utcTime) {
      return utcTime.toHoursMinutesIso()
    }
    const initialTime = legacyParseAdapter(timeParam, Formats.HoursMinutesTwelveHours)
    if (isValid(initialTime)) {
      return Timezone.omit(TimeOnly.fromJsDate(initialTime).toHoursMinutesIso())
    }
  }
  return ReservationWidgetConst.AllTimesOption
}

function getInitialHalo() {
  const haloParam = searchParams.get('halo')
  let haloTimeIntervalMinutes = parseInt(haloParam || '')
  if (Number.isNaN(haloTimeIntervalMinutes)) {
    haloTimeIntervalMinutes = ReservationWidgetConst.HaloMinutesOptions[ReservationWidgetConst.HaloMinutesOptions.length - 1] as number
  }

  return haloTimeIntervalMinutes
}

function getInitialSearchTab(isPrivateEventsEnabled: boolean, isExperiencesEnabled: boolean) {
  const tabParam = searchParams.get('searchTab')
  if (tabParam === 'group_bookings' && isPrivateEventsEnabled) {
    return 'group_bookings'
  }
  if (tabParam === 'experiences' && isExperiencesEnabled) {
    return 'experiences'
  }
  return 'reservations'
}

export function getInitialDuration({
  minimumSearchDuration,
  maximumSearchDuration,
  defaultSearchDuration,
}: {
  minimumSearchDuration: number
  maximumSearchDuration: number
  defaultSearchDuration?: number
}) {
  const duration = Number(searchParams.get('duration')) ?? undefined
  if (duration && duration >= minimumSearchDuration && duration <= maximumSearchDuration) {
    return duration
  }
  if (defaultSearchDuration && defaultSearchDuration >= minimumSearchDuration && defaultSearchDuration <= maximumSearchDuration) {
    return defaultSearchDuration
  }
  return minimumSearchDuration
}

export function adjustBirthdayString(value?: string) {
  if (!value) {
    return ''
  }
  if (value.length === 1) {
    return `0${value}`
  }
  return value
}
